import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Users, Handshake, TrendingUp, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const IMAGES = {
  about: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80",
  team: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80",
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Quality Focus',
      description: 'Uncompromising commitment to delivering only the finest agricultural products.'
    },
    {
      icon: Handshake,
      title: 'Trust & Reliability',
      description: 'Building lasting partnerships through transparent and honest business practices.'
    },
    {
      icon: Users,
      title: 'Customer First',
      description: 'Every decision is guided by our commitment to buyer satisfaction and success.'
    },
    {
      icon: TrendingUp,
      title: 'Continuous Growth',
      description: 'Constantly improving our processes to serve you better in the global market.'
    }
  ];

  const highlights = [
    'India-based export company with strong agricultural roots',
    'Direct partnerships with farmers across major growing regions',
    'Comprehensive quality management at every step',
    'Export documentation and compliance expertise',
    'Flexible packaging solutions for diverse market requirements',
    'Dedicated support for international buyers'
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-primary">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-accent font-medium text-sm tracking-wider uppercase">About Us</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 mb-6">
              Your Trusted Partner for Indian Agricultural Exports
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Nirmal Exports is an India-based export company specializing in premium quality 
              cotton and agricultural products for international markets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mb-6">
                Connecting Indian Agriculture to Global Markets
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Based in Maharashtra, India, Nirmal Exports has established itself as a reliable 
                  name in the agricultural export industry. Our journey began with a simple mission: 
                  to bring the finest Indian agricultural products to discerning buyers worldwide.
                </p>
                <p>
                  We specialize in raw cotton, processed cotton products, premium seeds and nuts, 
                  and high-quality dehydrated vegetables. Our strength lies in our extensive network 
                  of farmers and suppliers across India's key agricultural regions.
                </p>
                <p>
                  What sets us apart is our unwavering commitment to quality and our understanding 
                  of international trade requirements. We work closely with our buyers to ensure 
                  every shipment meets their exact specifications and arrives on time.
                </p>
              </div>
              
              <Link to="/contact" className="inline-block mt-8">
                <Button 
                  className="btn-secondary text-white font-semibold"
                  data-testid="about-contact-btn"
                >
                  Start a Conversation
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img 
                  src={IMAGES.about}
                  alt="Agricultural Export Operations"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <div className="font-heading font-bold text-2xl text-primary">25+</div>
                      <div className="text-muted-foreground text-sm">Export Destinations</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-28 bg-surface">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            {...fadeInUp}
            viewport={{ once: true }}
          >
            <span className="text-secondary font-medium text-sm tracking-wider uppercase">Our Values</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mt-3 mb-4">
              Principles That Guide Us
            </h2>
            <p className="text-muted-foreground text-lg">
              Our core values shape every aspect of how we do business
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-8 h-8 text-secondary" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-primary mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-20 lg:py-28">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img 
                src={IMAGES.team}
                alt="Our Team"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-primary mb-6">
                Why Buyers Choose Us
              </h2>
              <ul className="space-y-4">
                {highlights.map((highlight, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground">{highlight}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Let's Build a Partnership
            </h2>
            <p className="text-white/80 text-lg mb-8">
              We're committed to understanding your requirements and delivering exactly what you need. 
              Reach out to discuss how we can serve your business.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-accent text-primary hover:bg-accent/90 font-semibold px-8"
                data-testid="about-cta-btn"
              >
                Contact Our Team
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
